import { useState, useEffect } from "react";
import "./index.scss";
import { Table, Spin, Alert, Pagination } from "antd";
import type { TableProps, PaginationProps } from "antd";
import { getMembers, MembersData, MembersResponse } from "@/api/custom";

const Members: React.FC = () => {
  const [members, setMembers] = useState<MembersData[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);

  const [currentPage, setCurrentPage] = useState(1);
  const [pageSize, setPageSize] = useState(10);

  useEffect(() => {
    const fetchMembersData = async () => {
      try {
        setLoading(true);
        const membersData: MembersResponse = await getMembers();
        setMembers(membersData.members);
      } catch {
        setError("Failed to load members data");
      } finally {
        setLoading(false);
      }
    };
    fetchMembersData();
  }, []);

  const columns: TableProps<MembersData>["columns"] = [
    {
      title: "Name",
      dataIndex: "name",
      key: "name",
    },
    {
      title: "Email",
      dataIndex: "email",
      key: "email",
    },
    {
      title: "Role",
      dataIndex: "role",
      key: "role",
    },
  ];

  const paginatedData = members.slice(
    (currentPage - 1) * pageSize,
    currentPage * pageSize,
  );

  const handlePaginationChange: PaginationProps["onChange"] = (
    page,
    pageSize,
  ) => {
    setCurrentPage(page);
    setPageSize(pageSize);
  };

  if (error) {
    return <Alert message="Error" description={error} type="error" showIcon />;
  }

  const rowClassName = (_: MembersData, index: number): string => {
    return index % 2 === 0 ? "even-row" : "odd-row";
  };

  return (
    <div className="members">
      <div className="table">
        <Spin spinning={loading} tip="Loading data...">
          <Table
            columns={columns}
            dataSource={paginatedData.map((member) => ({
              ...member,
              key: member.id,
            }))}
            rowClassName={rowClassName}
            pagination={false}
            footer={() => (
              <div
                style={{
                  display: "flex",
                  justifyContent: "space-between",
                  flexDirection: "row-reverse",
                  alignItems: "center",
                }}
              >
                <div>
                  <Pagination
                    total={members.length}
                    current={currentPage}
                    pageSize={pageSize}
                    showTotal={(total) => `Total ${total}`}
                    onChange={handlePaginationChange}
                  />
                </div>
              </div>
            )}
          />
        </Spin>
      </div>
    </div>
  );
};

export default Members;
